import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';

import ThreeBounce from '../../ThreeBounce';
import { AutosuggestWrap } from './SchoolSearchV3Styles';
import { alwaysShowSchoolNotFound, defaultCAccounts, defaultInstitutionId } from '../../../destructured-front-end-config';

const isNullOrUndefined = (val) => val === undefined || val === null;
const keyExistsAsString = (val) => typeof val !== 'undefined' && typeof val === 'string';

const SchoolSearchV3 = ({
  id, width, value, hasError, onChange, onSelect, onBlur, brand, searchUrl, iamApiUrl, disabled, maxLength,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previousSearch, setPreviousSearch] = useState('');
  const lastRequestId = useRef(null);

  // eslint-disable-next-line no-shadow
  const onSuggestionsFetchRequested = async ({ value }) => {
    const instType = brand.short === 'bfw' ? 'hs' : 'college';
    if (lastRequestId.current !== null) {
      clearTimeout(lastRequestId.current);
    }
    // don't initiate new search if text hasn't changed
    if (previousSearch.trim() === value.trim()) {
      return null;
    }
    const url = `${iamApiUrl.endsWith('/') ? iamApiUrl.substr(0, searchUrl.length - 1) : iamApiUrl}/api/v2/school-search?schoolType=${instType === 'college' ? 'he' : 'hs'}&searchQuery=${encodeURIComponent(value)}`;

    setIsLoading(true);

    if (process.env.MOCK_SCHOOLSEARCH === 'true') {
      lastRequestId.current = setTimeout(() => {
        const matchedSchools = require(`../../../../mocks/schools-${instType}.json`).results.filter((s) => s.name.toLowerCase().includes(value.toLowerCase()));
        const snfItem = {
          name: 'School Not Found',
          cAccount: defaultCAccounts[0],
          accountId: defaultInstitutionId,
        };

        const activeSuggestions = matchedSchools?.length
          ? matchedSchools
          : [snfItem];

        if (alwaysShowSchoolNotFound && (activeSuggestions[0].name !== snfItem.name)) {
          activeSuggestions.push(snfItem);
        }

        setSuggestions(activeSuggestions);
        setPreviousSearch(value);
        setIsLoading(false);
      }, 300);
    } else {
      lastRequestId.current = setTimeout(() => {
        axios({
          method: 'GET',
          url,
          // allow 2XX and 5XX status codes
          validateStatus: (status) => (status >= 200 && status < 300) || (status >= 500 && status < 600),
        }).then((response) => {
          const snfItem = {
            name: 'School Not Found',
            prettyName: 'School Not Found',
            objectId: null,
            cAccount: defaultCAccounts[0],
            accountId: defaultInstitutionId,
          };

          const activeSuggestions = response.data?.results || [snfItem];

          // if FEATUREFLAG_ALWAYS_SHOW_SCHOOL_NOT_FOUND is true, and the first
          // suggestion is not the school not found item, add it to the list
          if (alwaysShowSchoolNotFound && (activeSuggestions[0].name !== snfItem.name)) {
            activeSuggestions.push(snfItem);
          }

          setSuggestions(activeSuggestions);
          setPreviousSearch(value);
          setIsLoading(false);
        }).catch((error) => {
          console.error(error);
        });
      }, 300);
    }
    return lastRequestId.current;
  };

  const processSelect = (suggestion) => {
    const lcCAccount = suggestion.cAccount?.toLowerCase();

    if (!defaultCAccounts.some((c) => c.toLowerCase() === lcCAccount)) {
      onSelect(suggestion);
    } else {
      onSelect({
        name: suggestion.name,
        cAccount: defaultCAccounts[0],
        accountId: suggestion.accountId || defaultInstitutionId,
      });
    }
  };

  const handleSchoolChange = (change) => {
    // Keydowns also generate change events with undefined newValue -- ignore
    if (isNullOrUndefined(change.newValue) && isNullOrUndefined(change.name)) return;

    // scenarios where newValue is present and defined are changes
    // This indicates the user freely typed in a school name
    if (keyExistsAsString(change.newValue)) {
      onChange({
        schoolName: change.newValue,
        cAccount: (change.cAccount || defaultCAccounts[0]),
        // these won't have a value for inst id, but we need to
        // overwrite any existing value in state
        institutionId: defaultInstitutionId,
      });

      return;
    }

    // this is basically clicking on a dropdown item
    onChange({
      schoolName: change.prettyName || change.name,
      cAccount: change.cAccount,
      institutionId: change.accountId,
    });

    processSelect(change);
  };

  const getSuggestionValue = (suggestion) => {
    const lcCAccount = suggestion.cAccount?.toLowerCase();

    // return "value" if nothing was actually selected from the dropdown
    if (defaultCAccounts.some((c) => c.toLowerCase() === lcCAccount) && (suggestion.name?.toLowerCase() !== 'school not found')) {
      return value;
    }

    // this might be uncomfortably hacky
    handleSchoolChange(suggestion);
    return suggestion.text;
  };

  const renderSuggestion = ({
    // eslint-disable-next-line react/prop-types
    name, prettyName, city, state, stateCode,
  }) => {
    let cityString = `${city}, ${stateCode || state}`;
    if (stateCode === false || state === null) {
      cityString = city;
    } else if (!city) {
      cityString = '';
    }
    return (
      <div className="schoolItem">
        <span>{prettyName || name}</span>
        <span className="cityState">{cityString}</span>
      </div>
    );
  };

  const inputProps = {
    placeholder: 'Start typing your school name',
    value,
    id,
    onChange: (e, change) => handleSchoolChange(change),
    onBlur,
    disabled,
    maxLength,
  };
  return (
    <AutosuggestWrap width={width} hasError={hasError}>
      {isLoading && <div className="loader"><ThreeBounce color="#da1b2c" size="12" /></div>}
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={() => setSuggestions([])}
        onSuggestionSelected={(e, { suggestion }) => handleSchoolChange(suggestion)}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        maxLength={maxLength}
      />
    </AutosuggestWrap>
  );
};

SchoolSearchV3.propTypes = {
  id: PropTypes.string.isRequired,
  width: PropTypes.string,
  value: PropTypes.string.isRequired,
  hasError: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  brand: PropTypes.shape({
    short: PropTypes.string.isRequired,
  }).isRequired,
  searchUrl: PropTypes.string.isRequired,
  iamApiUrl: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
};

SchoolSearchV3.defaultProps = {
  width: '420px',
  onBlur: () => {},
  disabled: false,
  maxLength: 150,
};

export default SchoolSearchV3;
